import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
    UserPlus, Check, X, Clock, Loader2, Shield, Trophy,
    School, Building2, Mail, Phone, MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
    federation_admin: { label: 'Federation Admin', color: 'text-blue-400 bg-blue-400/10 border-blue-400/30', icon: Trophy },
    club_admin: { label: 'Club Admin', color: 'text-purple-400 bg-purple-400/10 border-purple-400/30', icon: Shield },
    school_admin: { label: 'School Admin', color: 'text-green-400 bg-green-400/10 border-green-400/30', icon: School },
    nock_admin: { label: 'NOCK Admin', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30', icon: Building2 },
    education_admin: { label: 'Ministry of Education', color: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30', icon: Building2 },
    ministry_admin: { label: 'Ministry of Sports', color: 'text-orange-400 bg-orange-400/10 border-orange-400/30', icon: Building2 },
};

const STATUS_STYLES = {
    pending: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
    approved: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30',
    rejected: 'text-red-400 bg-red-400/10 border-red-400/30',
};

const ApplicationsTab = ({ token, userRole }) => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [processing, setProcessing] = useState(null);

    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: { Authorization: `Bearer ${token}` }
    });

    const loadApplications = async () => {
        setLoading(true);
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const res = await api.get('/auth/admin-applications', { params });
            setApplications(res.data.applications || []);
        } catch (err) {
            console.error('Failed to load applications:', err);
            setApplications([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadApplications(); }, [filter]);

    const handleApprove = async (appId, name) => {
        setProcessing(appId);
        try {
            await api.put(`/auth/admin-applications/${appId}/approve`);
            toast.success(`Approved: ${name} can now log in`);
            loadApplications();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to approve');
        } finally {
            setProcessing(null);
        }
    };

    const handleReject = async (appId, name) => {
        const reason = window.prompt(`Reason for rejecting ${name}?`);
        if (reason === null) return;
        setProcessing(appId);
        try {
            await api.put(`/auth/admin-applications/${appId}/reject?reason=${encodeURIComponent(reason)}`);
            toast.success(`Rejected: ${name}`);
            loadApplications();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to reject');
        } finally {
            setProcessing(null);
        }
    };

    if (userRole !== 'master_admin') {
        return (
            <div className="text-center py-12 text-muted-foreground" data-testid="applications-restricted">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Only the Master Admin can manage admin applications.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="applications-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-primary" />
                        ADMIN APPLICATIONS
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                        Review and approve admin account requests
                    </p>
                </div>
                <div className="flex gap-2">
                    {['pending', 'approved', 'rejected', 'all'].map(s => (
                        <Button
                            key={s}
                            variant={filter === s ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setFilter(s)}
                            data-testid={`filter-${s}`}
                        >
                            {s === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                            {s === 'approved' && <Check className="w-3 h-3 mr-1" />}
                            {s === 'rejected' && <X className="w-3 h-3 mr-1" />}
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                        </Button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
            ) : applications.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground" data-testid="no-applications">
                    <UserPlus className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p>No {filter !== 'all' ? filter : ''} applications found</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {applications.map(app => {
                        const roleInfo = ROLE_LABELS[app.requested_role] || { label: app.requested_role, color: 'text-gray-400', icon: Shield };
                        const RoleIcon = roleInfo.icon;
                        return (
                            <div
                                key={app.id}
                                className="bg-[#0d2847] border border-white/10 rounded-lg p-5"
                                data-testid={`application-${app.id}`}
                            >
                                <div className="flex flex-col md:flex-row justify-between gap-4">
                                    <div className="flex-1 space-y-3">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <h3 className="font-semibold text-lg">{app.full_name}</h3>
                                            <span className={`text-xs px-2 py-0.5 rounded border ${roleInfo.color}`}>
                                                <RoleIcon className="w-3 h-3 inline mr-1" />
                                                {roleInfo.label}
                                            </span>
                                            <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_STYLES[app.status] || ''}`}>
                                                {app.status}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                                <Mail className="w-3 h-3" /> {app.email}
                                            </div>
                                            {app.phone && (
                                                <div className="flex items-center gap-2 text-muted-foreground">
                                                    <Phone className="w-3 h-3" /> {app.phone}
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                                <Building2 className="w-3 h-3" /> {app.organization_name}
                                            </div>
                                            {app.county && (
                                                <div className="flex items-center gap-2 text-muted-foreground">
                                                    <MapPin className="w-3 h-3" /> {app.county}
                                                </div>
                                            )}
                                            {app.sport && (
                                                <div className="flex items-center gap-2 text-muted-foreground">
                                                    <Trophy className="w-3 h-3" /> {app.sport}
                                                </div>
                                            )}
                                        </div>

                                        {app.reason && (
                                            <p className="text-sm bg-white/5 p-3 rounded border border-white/5 italic">
                                                "{app.reason}"
                                            </p>
                                        )}

                                        {app.rejection_reason && (
                                            <p className="text-sm text-red-400 bg-red-400/5 p-3 rounded border border-red-400/10">
                                                Rejection reason: {app.rejection_reason}
                                            </p>
                                        )}

                                        <p className="text-xs text-muted-foreground">
                                            Submitted: {new Date(app.submitted_at).toLocaleDateString('en-KE', { dateStyle: 'medium' })}
                                            {app.reviewed_at && ` | Reviewed: ${new Date(app.reviewed_at).toLocaleDateString('en-KE', { dateStyle: 'medium' })}`}
                                        </p>
                                    </div>

                                    {/* Actions */}
                                    {app.status === 'pending' && (
                                        <div className="flex md:flex-col gap-2 shrink-0">
                                            <Button
                                                onClick={() => handleApprove(app.id, app.full_name)}
                                                disabled={processing === app.id}
                                                className="bg-emerald-600 hover:bg-emerald-700"
                                                data-testid={`approve-${app.id}`}
                                            >
                                                {processing === app.id ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <><Check className="w-4 h-4 mr-1" /> Approve</>
                                                )}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={() => handleReject(app.id, app.full_name)}
                                                disabled={processing === app.id}
                                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                                data-testid={`reject-${app.id}`}
                                            >
                                                <X className="w-4 h-4 mr-1" /> Reject
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ApplicationsTab;
